/**
 * The kits pitch as a picture, the companion to Blueprint: one sandbox wall, three slots a kit fills with your own
 * pieces (agent harness, tools, capabilities such as skills, context, network endpoints and SSH forwarding), and the
 * things no kit can change drawn as the wall's fixed guarantees.
 */
import { color, font } from './tokens.ts';

type Props = {
  /** Slide the slots in one after another (off for reduced motion). */
  animate?: boolean;
  title?: string;
  className?: string;
};

const mono = { fontFamily: font.mono } as const;
const ui = { fontFamily: font.ui } as const;

function Label({ x, y, children, fill = color.accentSoft }: { x: number; y: number; children: string; fill?: string }) {
  return (
    <text x={x} y={y} fill={fill} style={{ ...mono, fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {children}
    </text>
  );
}

function Chip({ x, y, w, label }: { x: number; y: number; w: number; label: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={20} rx={5} fill={color.bg} stroke={color.line} />
      <text x={x + w / 2} y={y + 13.5} textAnchor="middle" fill={color.text} style={{ ...mono, fontSize: 9 }}>
        {label}
      </text>
    </g>
  );
}

function AgentIcon({ x, y, s = 26 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <line x1={0} y1={-s * 0.42} x2={0} y2={-s * 0.58} stroke={color.accentSoft} strokeWidth={2} />
      <circle cx={0} cy={-s * 0.63} r={s * 0.07} fill={color.accentSoft} />
      <rect x={-s * 0.45} y={-s * 0.42} width={s * 0.9} height={s * 0.84} rx={s * 0.26} fill={color.brand} stroke={color.brandBright} strokeWidth={1.5} />
      <rect x={-s * 0.32} y={-s * 0.24} width={s * 0.64} height={s * 0.34} rx={s * 0.14} fill={color.bg} />
      <circle cx={-s * 0.13} cy={-s * 0.07} r={s * 0.055} fill={color.text} />
      <circle cx={s * 0.13} cy={-s * 0.07} r={s * 0.055} fill={color.text} />
    </g>
  );
}

function TerminalIcon({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} fill="none" strokeLinecap="round" strokeLinejoin="round">
      <rect x={-14} y={-11} width={28} height={22} rx={4} fill={color.brand} stroke={color.brandBright} strokeWidth={1.5} />
      <path d="M-8 -3 L-3 1 L-8 5" stroke={color.text} strokeWidth={1.8} />
      <path d="M0 5 H8" stroke={color.text} strokeWidth={1.8} />
    </g>
  );
}

/** Capabilities as a set of pieces: three snapped into a grid, one more being added. */
function CapabilityIcon({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x={-14} y={-11} width={28} height={22} rx={4} fill={color.brand} stroke={color.brandBright} strokeWidth={1.5} />
      <rect x={-9} y={-7} width={7} height={6} rx={1.5} fill={color.text} />
      <rect x={2} y={-7} width={7} height={6} rx={1.5} fill={color.text} />
      <rect x={-9} y={2} width={7} height={6} rx={1.5} fill={color.text} />
      <rect x={2} y={2} width={7} height={6} rx={1.5} fill="none" stroke={color.accentSoft} strokeDasharray="2 1.5" />
    </g>
  );
}

/** The kit itself: a small cube, the same mark the blueprint uses for the sandbox's own engine. */
function KitIcon({ x, y }: { x: number; y: number }) {
  return (
    <path
      transform={`translate(${x} ${y})`}
      d="M0 -7 L6.5 -3.5 V3.5 L0 7 L-6.5 3.5 V-3.5 Z M-6.5 -3.5 L0 0 L6.5 -3.5 M0 0 V7"
      fill="none"
      stroke="#fff"
      strokeWidth={1.3}
      strokeLinejoin="round"
    />
  );
}

const SLOTS = [
  { tag: 'Harness', title: 'Bring your own agent', icon: 'agent', chips: [[['Claude', 48], ['Codex', 46], ['your own', 58]]] },
  { tag: 'Tools', title: 'Add your own tools', icon: 'terminal', chips: [[['compilers', 62], ['CLIs', 40], ['browser', 52]]] },
  {
    tag: 'Capabilities',
    title: 'Grant it capabilities',
    icon: 'capability',
    chips: [
      [['skills', 44], ['context', 52], ['endpoints', 62], ['SSH agent', 62]],
      [['credentials', 74], ['git identity', 70], ['host mount', 64]],
    ],
  },
] as const;

/** Each chip row past the first makes its slot this much taller. */
const CHIP_ROW = 26;
const slotHeight = (i: number) => 80 + (SLOTS[i].chips.length - 1) * CHIP_ROW;

const FIXED = ['Agent stays inside the walls', 'Real keys stay outside the box', 'Every request goes through the guard'] as const;

const ROW_Y = (i: number) => SLOTS.slice(0, i).reduce((y, _, k) => y + slotHeight(k) + 8, 52);
const KIT_BOTTOM = ROW_Y(SLOTS.length);
const FIXED_Y = (i: number) => KIT_BOTTOM + 28 + i * 30;
const FIXED_BOTTOM = FIXED_Y(2) + 36;

export function KitDiagram({ animate = false, title = 'What a kit can change in the sandbox', className }: Props) {
  /** Staggered reveal: slots one by one, then the fixed walls. */
  const layer = (i: number) => (animate ? { className: 'bp-new', style: { animationDelay: `${i * 0.4}s` } } : {});
  const desc =
    'Kits customize the box. A kit fills three slots inside the sandbox with your own pieces: the agent harness you already use, the tools the work needs, and capabilities such as skills, context, network endpoints, SSH forwarding, credentials, git identity and host mounts. ' +
    'What no kit changes: the agent stays inside the walls, real keys stay outside the box, and every network request goes through the guard.';

  return (
    <svg viewBox={`0 0 360 ${FIXED_BOTTOM + 8}`} className={className ?? 'blueprint'} role="img" aria-label={`${title}. ${desc}`}>
      <defs>
        <pattern id="kit-dots" width="10" height="10" patternUnits="userSpaceOnUse">
          <circle cx="5" cy="5" r="0.6" fill="rgba(170,196,248,0.18)" />
        </pattern>
      </defs>

      {/* The wall: fixed, the same for every kit */}
      <rect x={6} y={6} width={348} height={FIXED_BOTTOM - 4} rx={14} fill="url(#kit-dots)" stroke={color.accentSoft} strokeWidth={2.5} />
      <Label x={18} y={26}>Your sandbox</Label>

      {/* The kit: the region it customizes, named before the walls it can't touch */}
      <rect x={14} y={34} width={332} height={KIT_BOTTOM - 34} rx={12} fill="rgba(29,99,237,0.07)" stroke={color.brandBright} strokeOpacity={0.6} />
      <g {...layer(0)}>
        <rect x={166} y={24} width={172} height={20} rx={10} fill={color.brand} stroke={color.brandBright} />
        <KitIcon x={182} y={34} />
        <text x={194} y={37.2} fill="#fff" style={{ ...mono, fontSize: 9, letterSpacing: '0.06em' }}>KITS CUSTOMIZE THE BOX</text>
      </g>

      {/* The three slots a kit fills */}
      {SLOTS.map((s, i) => {
        const y = ROW_Y(i);
        const h = slotHeight(i);
        return (
          <g key={s.tag} {...layer(i)} data-testid={`kit-${i + 1}`}>
            <rect x={20} y={y} width={320} height={h} rx={10} fill="none" stroke={color.accentSoft} strokeDasharray="4 3" opacity={0.6} />
            <rect x={28} y={y + 6} width={304} height={h - 12} rx={8} fill={color.panel} stroke={color.brandBright} />
            <g>
              {s.icon === 'agent' && <AgentIcon x={54} y={y + 42} />}
              {s.icon === 'terminal' && <TerminalIcon x={54} y={y + 40} />}
              {s.icon === 'capability' && <CapabilityIcon x={54} y={y + 40} />}
            </g>
            <Label x={80} y={y + 24}>{s.tag}</Label>
            <text x={80} y={y + 42} fill={color.text} style={{ ...ui, fontSize: 13, fontWeight: 700 }}>{s.title}</text>
            {s.chips.map((row, r) => {
              let cx = 80;
              return row.map(([label, w]) => {
                const chip = <Chip key={label} x={cx} y={y + 48 + r * CHIP_ROW} w={w} label={label} />;
                cx += w + 6;
                return chip;
              });
            })}
            <rect x={282} y={y + 12} width={42} height={16} rx={8} fill={color.brand} />
            <text x={303} y={y + 23.5} textAnchor="middle" fill="#fff" style={{ ...mono, fontSize: 8, letterSpacing: '0.06em' }}>YOURS</text>
          </g>
        );
      })}

      {/* What no kit changes */}
      <g {...layer(3)} data-testid="kit-fixed">
        <Label x={20} y={KIT_BOTTOM + 20} fill={color.ok}>What no kit changes</Label>
        {FIXED.map((f, i) => (
          <g key={f}>
            <rect x={20} y={FIXED_Y(i) + 6} width={320} height={24} rx={6} fill={color.panel} stroke={color.ok} strokeOpacity={0.5} />
            <text x={32} y={FIXED_Y(i) + 22} fill={color.ok} style={{ ...ui, fontSize: 11, fontWeight: 700 }}>✓</text>
            <text x={48} y={FIXED_Y(i) + 22} fill={color.text} style={{ ...ui, fontSize: 11 }}>{f}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}
